"use client";

import { c as _c } from "react/compiler-runtime";
import { Checkbox } from "@/components/ui/Checkbox";
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from "@/components/ui/ContextMenu";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/DropdownMenu";
import { IconButton } from "@/components/ui/IconButton";
import type { ActionMenuEntry } from "@/types/menu";
import type { Cell, Row } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { MoreVertical } from "lucide-react";
import Link from "next/link";
import React, { useId } from "react";
import type { ReactNode } from "react";
import cn from "twsx";
import styles from "./List.module.css";
import type { FilterListRowVariant, HorizontalOverflowState, ListColumnMeta, StickyColumnLayout } from "./FilterListComponent";
export const linkedRowClassName = styles.linked;
export function ListRowLink(t0) {
  const $ = _c(6);
  const {
    href,
    target,
    label,
    descriptionId
  } = t0;
  const t1 = target === "_blank" ? "noopener noreferrer" : undefined;
  const t2 = target === "_blank" ? `${label} (opens in a new tab)` : label;
  let t3;
  if ($[0] !== descriptionId || $[1] !== href || $[2] !== t1 || $[3] !== t2 || $[4] !== target) {
    t3 = <Link href={href} target={target} rel={t1} prefetch={false} aria-label={t2} aria-describedby={descriptionId} data-list-row-link="true" className={styles.rowLink} onContextMenu={_temp} />;
    $[0] = descriptionId;
    $[1] = href;
    $[2] = t1;
    $[3] = t2;
    $[4] = target;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function _temp(event) {
  return event.stopPropagation();
}
export function SelectionCheckbox(t0) {
  const $ = _c(8);
  const {
    ariaLabel,
    checked,
    isIndeterminate,
    className,
    onClick
  } = t0;
  let t1;
  if ($[0] !== onClick) {
    t1 = event => {
      event.stopPropagation();
      event.preventDefault();
      onClick(event);
    };
    $[0] = onClick;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== ariaLabel || $[3] !== checked || $[4] !== className || $[5] !== isIndeterminate || $[6] !== t1) {
    t2 = <Checkbox aria-label={ariaLabel} checked={checked} isIndeterminate={isIndeterminate} className={className} onClick={t1} />;
    $[2] = ariaLabel;
    $[3] = checked;
    $[4] = className;
    $[5] = isIndeterminate;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/** Where a pinned column draws its scroll mask and edge divider for the current horizontal scroll. */
export function getStickyEdgeState(layout: StickyColumnLayout | undefined, overflow: HorizontalOverflowState) {
  if (!layout?.isBoundary) return {};
  const scrolled = layout.side === "left" ? overflow.hasScrolledLeft : overflow.hasScrollableRight;
  return scrolled ? {
    "data-mask": layout.side,
    "data-boundary": layout.side
  } : {};
}

/** Pinned columns sit at a measured offset; this is the only per-cell geometry left inline. */
export function getStickyColumnStyle(layout: StickyColumnLayout | undefined, indent?: string): React.CSSProperties | undefined {
  if (!layout) return indent ? {
    paddingLeft: indent
  } : undefined;
  return {
    [layout.side]: layout.offset,
    width: layout.width,
    minWidth: layout.width,
    maxWidth: layout.width,
    paddingLeft: indent
  };
}
type RowMenuProps<$Row> = {
  actionButtons?: (row: $Row) => React.ReactNode;
  actionButtonsDisplay?: "hover" | "sticky";
  actionMenuTriggerVariant?: "ghost" | "secondary";
  getVisibleMenuItems: (row: $Row) => ActionMenuEntry<$Row>[];
  renderMenuEntries: (entries: ActionMenuEntry<$Row>[], row: $Row, variant: "dropdown" | "context") => ReactNode[];
};
type MemoizedRowCellsProps<$Row> = RowMenuProps<$Row> & {
  descriptionId: string;
  row: Row<$Row>;
  horizontalOverflowState: HorizontalOverflowState;
  stickyColumnLayouts: Map<string, StickyColumnLayout>;
};

/** Memoized row content — only the cells and action menu, skips re-render on selection changes. */
const MemoizedRowCellsInner = t0 => {
  const $ = _c(33);
  const {
    descriptionId,
    row,
    actionButtons,
    actionButtonsDisplay,
    actionMenuTriggerVariant: t1,
    getVisibleMenuItems,
    renderMenuEntries,
    horizontalOverflowState,
    stickyColumnLayouts
  } = t0;
  const actionMenuTriggerVariant = t1 === undefined ? "ghost" : t1;
  const rowLevel = row.original && typeof row.original === "object" && "level" in row.original && typeof row.original.level === "number" ? row.original.level : null;
  let actionButtonsContent;
  let menuEntries;
  let rowMenu;
  let t2;
  if ($[0] !== actionButtons || $[1] !== actionButtonsDisplay || $[2] !== actionMenuTriggerVariant || $[3] !== descriptionId || $[4] !== getVisibleMenuItems || $[5] !== horizontalOverflowState || $[6] !== renderMenuEntries || $[7] !== row || $[8] !== rowLevel || $[9] !== stickyColumnLayouts) {
    menuEntries = getVisibleMenuItems(row.original);
    let t3;
    if ($[14] !== actionButtons || $[15] !== row.original) {
      t3 = actionButtons?.(row.original);
      $[14] = actionButtons;
      $[15] = row.original;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    actionButtonsContent = t3;
    const visibleCells = row.getVisibleCells();
    const firstRightStickyCellId = visibleCells.find(cell => stickyColumnLayouts.get(cell.column.id)?.side === "right")?.id;
    rowMenu = menuEntries.length > 0 && <DropdownMenu modal={true}><DropdownMenuTrigger asChild={true}><IconButton label="Row actions" variant={actionMenuTriggerVariant} onClick={actionButtonsDisplay === "sticky" ? undefined : _temp2}><MoreVertical /></IconButton></DropdownMenuTrigger><DropdownMenuContent align="end">{renderMenuEntries(menuEntries, row.original, "dropdown")}</DropdownMenuContent></DropdownMenu>;
    t2 = visibleCells.map((cell_0, index) => {
      const stickyLayout = stickyColumnLayouts.get(cell_0.column.id);
      const meta = cell_0.column.columnDef.meta as ListColumnMeta | undefined;
      return <React.Fragment key={cell_0.id}>{cell_0.id === firstRightStickyCellId && <div aria-hidden="true" className="h-full" />}<div id={index === 0 ? descriptionId : undefined} data-column-id={cell_0.column.id} data-sticky-column={stickyLayout?.side} {...getStickyEdgeState(stickyLayout, horizontalOverflowState)} className={cn(styles.cell, stickyLayout?.side === "right" && "justify-end px-3", meta?.align === "right" && "justify-end text-right", meta?.multiline && "py-2.5")} style={getStickyColumnStyle(stickyLayout, index === 0 && rowLevel ? `calc(${rowLevel} * var(--list-row-indent-step, 16px))` : undefined)}>{stickyLayout ? <div className={cn(styles.pinned, "min-w-0 w-full truncate", stickyLayout.side === "right" && "flex justify-end")}>{flexRender(cell_0.column.columnDef.cell, cell_0.getContext())}</div> : <div className={cn("min-w-0 truncate", meta?.align !== "right" && "flex-1")}>{flexRender(cell_0.column.columnDef.cell, cell_0.getContext())}</div>}</div></React.Fragment>;
    });
    $[0] = actionButtons;
    $[1] = actionButtonsDisplay;
    $[2] = actionMenuTriggerVariant;
    $[3] = descriptionId;
    $[4] = getVisibleMenuItems;
    $[5] = horizontalOverflowState;
    $[6] = renderMenuEntries;
    $[7] = row;
    $[8] = rowLevel;
    $[9] = stickyColumnLayouts;
    $[10] = actionButtonsContent;
    $[11] = menuEntries;
    $[12] = rowMenu;
    $[13] = t2;
  } else {
    actionButtonsContent = $[10];
    menuEntries = $[11];
    rowMenu = $[12];
    t2 = $[13];
  }
  let t3;
  if ($[17] !== actionButtons || $[18] !== actionButtonsContent || $[19] !== actionButtonsDisplay || $[20] !== horizontalOverflowState || $[21] !== menuEntries || $[22] !== rowMenu) {
    t3 = actionButtonsDisplay === "sticky" && (actionButtons || menuEntries.length > 0) && <div data-list-row-actions="true" data-sticky-column="right" data-boundary={horizontalOverflowState.hasScrollableRight ? "right" : undefined} className={styles.actions} onClick={_temp3}>{actionButtonsContent}{rowMenu}</div>;
    $[17] = actionButtons;
    $[18] = actionButtonsContent;
    $[19] = actionButtonsDisplay;
    $[20] = horizontalOverflowState;
    $[21] = menuEntries;
    $[22] = rowMenu;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  let t4;
  if ($[24] !== actionButtonsContent || $[25] !== actionButtonsDisplay || $[26] !== menuEntries || $[27] !== rowMenu) {
    t4 = actionButtonsDisplay !== "sticky" && (actionButtonsContent || menuEntries.length > 0) && <div className={styles.hoverActions}>{actionButtonsContent && <div className="flex flex-row items-center gap-1" onClick={_temp4}>{actionButtonsContent}</div>}{rowMenu}</div>;
    $[24] = actionButtonsContent;
    $[25] = actionButtonsDisplay;
    $[26] = menuEntries;
    $[27] = rowMenu;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  let t5;
  if ($[29] !== t2 || $[30] !== t3 || $[31] !== t4) {
    t5 = <>{t2}{t3}{t4}</>;
    $[29] = t2;
    $[30] = t3;
    $[31] = t4;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  return t5;
};
const MemoizedRowCells = React.memo(MemoizedRowCellsInner, (prev, next) => prev.row.id === next.row.id && prev.row.original === next.row.original && prev.actionButtons === next.actionButtons && prev.actionButtonsDisplay === next.actionButtonsDisplay && prev.actionMenuTriggerVariant === next.actionMenuTriggerVariant && prev.getVisibleMenuItems === next.getVisibleMenuItems && prev.renderMenuEntries === next.renderMenuEntries && prev.horizontalOverflowState === next.horizontalOverflowState && prev.stickyColumnLayouts === next.stickyColumnLayouts) as typeof MemoizedRowCellsInner;
type FilterListRowProps<$Row> = RowMenuProps<$Row> & {
  row: Row<$Row>;
  rowIndex: number;
  isSelected: boolean;
  isPreviousRowSelected: boolean;
  isNextRowSelected: boolean;
  enableRowSelection: boolean;
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  hasClickHandler: boolean;
  onRowClick: (e: React.MouseEvent, row: Row<$Row>, rowIndex: number) => void;
  onCheckboxClick: (e: React.MouseEvent, rowIndex: number) => void;
  horizontalOverflowState: HorizontalOverflowState;
  showSelectionBoundary: boolean;
  stickyColumnLayouts: Map<string, StickyColumnLayout>;
  variant: FilterListRowVariant;
  rowStyle?: React.CSSProperties;
  hideDivider?: boolean;
};

/** One table row: selection, full-bleed row band and memoized cell content. */
const FilterListRowInner = t0 => {
  const $ = _c(110);
  const {
    row,
    rowIndex,
    isSelected,
    isPreviousRowSelected,
    isNextRowSelected,
    enableRowSelection,
    href,
    target,
    hasClickHandler,
    onRowClick,
    onCheckboxClick,
    actionButtons,
    actionButtonsDisplay,
    actionMenuTriggerVariant,
    getVisibleMenuItems,
    renderMenuEntries,
    horizontalOverflowState,
    showSelectionBoundary,
    stickyColumnLayouts,
    variant,
    rowStyle,
    hideDivider: t1
  } = t0;
  const hideDivider = t1 === undefined ? false : t1;
  const descriptionId = useId();
  const t2 = row.original;
  let T0;
  let T1;
  let t3;
  let t4;
  let t5;
  if ($[0] !== actionButtons || $[1] !== actionButtonsDisplay || $[2] !== actionMenuTriggerVariant || $[3] !== descriptionId || $[4] !== enableRowSelection || $[5] !== getVisibleMenuItems || $[6] !== hasClickHandler || $[7] !== hideDivider || $[8] !== horizontalOverflowState || $[9] !== href || $[10] !== isNextRowSelected || $[11] !== isPreviousRowSelected || $[12] !== isSelected || $[13] !== onCheckboxClick || $[14] !== onRowClick || $[15] !== renderMenuEntries || $[16] !== row || $[17] !== rowIndex || $[18] !== rowStyle || $[19] !== showSelectionBoundary || $[20] !== stickyColumnLayouts || $[21] !== target || $[22] !== variant) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const menuEntries = getVisibleMenuItems(t2);
      const hasContextMenu = menuEntries.length > 0;
      let t10;
      let t11;
      let t12;
      let t13;
      let t14;
      let t15;
      let t16;
      let t17;
      let t18;
      let t19;
      let t6;
      let t7;
      let t8;
      let t9;
      if ($[28] !== actionButtons || $[29] !== descriptionId || $[30] !== enableRowSelection || $[31] !== hasClickHandler || $[32] !== hasContextMenu || $[33] !== hideDivider || $[34] !== horizontalOverflowState || $[35] !== href || $[36] !== isNextRowSelected || $[37] !== isPreviousRowSelected || $[38] !== isSelected || $[39] !== onCheckboxClick || $[40] !== onRowClick || $[41] !== row || $[42] !== rowIndex || $[43] !== rowStyle || $[44] !== showSelectionBoundary || $[45] !== target || $[46] !== variant) {
        const canSelectRow = row.getCanSelect();
        const hasRowAction = enableRowSelection && canSelectRow || hasClickHandler || Boolean(href) || hasContextMenu || Boolean(actionButtons);
        t6 = "true";
        t7 = row.id;
        t8 = isSelected || undefined;
        t9 = hasRowAction || undefined;
        t10 = hasClickHandler || Boolean(href) || undefined;
        t11 = isSelected && isPreviousRowSelected || undefined;
        t12 = isSelected && isNextRowSelected || undefined;
        t13 = hideDivider || undefined;
        t14 = variant === "summary" ? "summary" : undefined;
        const t20 = href && styles.linked;
        if ($[61] !== t20) {
          t15 = cn("group/row grid list-table-subgrid", styles.row, t20);
          $[61] = t20;
          $[62] = t15;
        } else {
          t15 = $[62];
        }
        if ($[63] !== rowStyle) {
          t16 = rowStyle && {
            ...rowStyle,
            ...(rowStyle.backgroundColor ? {
              "--list-row-bg": rowStyle.backgroundColor
            } : {})
          };
          $[63] = rowStyle;
          $[64] = t16;
        } else {
          t16 = $[64];
        }
        if ($[65] !== hasClickHandler || $[66] !== onRowClick || $[67] !== row || $[68] !== rowIndex) {
          t17 = hasClickHandler ? e => onRowClick(e, row, rowIndex) : undefined;
          $[65] = hasClickHandler;
          $[66] = onRowClick;
          $[67] = row;
          $[68] = rowIndex;
          $[69] = t17;
        } else {
          t17 = $[69];
        }
        if ($[70] !== descriptionId || $[71] !== href || $[72] !== rowIndex || $[73] !== target) {
          t18 = href && <ListRowLink href={href} target={target} label={`Open row ${rowIndex + 1}`} descriptionId={descriptionId} />;
          $[70] = descriptionId;
          $[71] = href;
          $[72] = rowIndex;
          $[73] = target;
          $[74] = t18;
        } else {
          t18 = $[74];
        }
        t19 = enableRowSelection && <div className={cn(styles.selection, canSelectRow && "cursor-pointer")} data-selected={isSelected || undefined} data-sticky-column="left" data-mask={horizontalOverflowState.hasScrolledLeft ? "left" : undefined} data-boundary={showSelectionBoundary ? "left" : undefined}><span className={cn(styles.pinned, "flex items-center justify-center")}>{canSelectRow && <SelectionCheckbox ariaLabel={`Select row ${rowIndex + 1}`} checked={isSelected} onClick={event => onCheckboxClick(event, rowIndex)} />}</span></div>;
        $[28] = actionButtons;
        $[29] = descriptionId;
        $[30] = enableRowSelection;
        $[31] = hasClickHandler;
        $[32] = hasContextMenu;
        $[33] = hideDivider;
        $[34] = horizontalOverflowState;
        $[35] = href;
        $[36] = isNextRowSelected;
        $[37] = isPreviousRowSelected;
        $[38] = isSelected;
        $[39] = onCheckboxClick;
        $[40] = onRowClick;
        $[41] = row;
        $[42] = rowIndex;
        $[43] = rowStyle;
        $[44] = showSelectionBoundary;
        $[45] = target;
        $[46] = variant;
        $[47] = t10;
        $[48] = t11;
        $[49] = t12;
        $[50] = t13;
        $[51] = t14;
        $[52] = t15;
        $[53] = t16;
        $[54] = t17;
        $[55] = t18;
        $[56] = t19;
        $[57] = t6;
        $[58] = t7;
        $[59] = t8;
        $[60] = t9;
      } else {
        t10 = $[47];
        t11 = $[48];
        t12 = $[49];
        t13 = $[50];
        t14 = $[51];
        t15 = $[52];
        t16 = $[53];
        t17 = $[54];
        t18 = $[55];
        t19 = $[56];
        t6 = $[57];
        t7 = $[58];
        t8 = $[59];
        t9 = $[60];
      }
      let t20;
      if ($[75] !== actionButtons || $[76] !== actionButtonsDisplay || $[77] !== actionMenuTriggerVariant || $[78] !== descriptionId || $[79] !== getVisibleMenuItems || $[80] !== horizontalOverflowState || $[81] !== renderMenuEntries || $[82] !== row || $[83] !== stickyColumnLayouts) {
        t20 = <MemoizedRowCells descriptionId={descriptionId} row={row} actionButtons={actionButtons} actionButtonsDisplay={actionButtonsDisplay} actionMenuTriggerVariant={actionMenuTriggerVariant} getVisibleMenuItems={getVisibleMenuItems} renderMenuEntries={renderMenuEntries} horizontalOverflowState={horizontalOverflowState} stickyColumnLayouts={stickyColumnLayouts} />;
        $[75] = actionButtons;
        $[76] = actionButtonsDisplay;
        $[77] = actionMenuTriggerVariant;
        $[78] = descriptionId;
        $[79] = getVisibleMenuItems;
        $[80] = horizontalOverflowState;
        $[81] = renderMenuEntries;
        $[82] = row;
        $[83] = stickyColumnLayouts;
        $[84] = t20;
      } else {
        t20 = $[84];
      }
      let t21;
      if ($[85] !== t10 || $[86] !== t11 || $[87] !== t12 || $[88] !== t13 || $[89] !== t14 || $[90] !== t15 || $[91] !== t16 || $[92] !== t17 || $[93] !== t18 || $[94] !== t19 || $[95] !== t20 || $[96] !== t6 || $[97] !== t7 || $[98] !== t8 || $[99] !== t9) {
        t21 = <div data-list-row={t6} data-list-row-id={t7} data-selected={t8} data-actionable={t9} data-clickable={t10} data-joined-top={t11} data-joined-bottom={t12} data-hide-divider={t13} data-variant={t14} className={t15} style={t16} onClick={t17}>{t18}{t19}{t20}</div>;
        $[85] = t10;
        $[86] = t11;
        $[87] = t12;
        $[88] = t13;
        $[89] = t14;
        $[90] = t15;
        $[91] = t16;
        $[92] = t17;
        $[93] = t18;
        $[94] = t19;
        $[95] = t20;
        $[96] = t6;
        $[97] = t7;
        $[98] = t8;
        $[99] = t9;
        $[100] = t21;
      } else {
        t21 = $[100];
      }
      const rowContent = t21;
      if (!hasContextMenu) {
        t5 = rowContent;
        break bb0;
      }
      T1 = ContextMenu;
      if ($[101] !== rowContent) {
        t4 = <ContextMenuTrigger asChild={true}>{rowContent}</ContextMenuTrigger>;
        $[101] = rowContent;
        $[102] = t4;
      } else {
        t4 = $[102];
      }
      T0 = ContextMenuContent;
      t3 = renderMenuEntries(menuEntries, row.original, "context");
    }
    $[0] = actionButtons;
    $[1] = actionButtonsDisplay;
    $[2] = actionMenuTriggerVariant;
    $[3] = descriptionId;
    $[4] = enableRowSelection;
    $[5] = getVisibleMenuItems;
    $[6] = hasClickHandler;
    $[7] = hideDivider;
    $[8] = horizontalOverflowState;
    $[9] = href;
    $[10] = isNextRowSelected;
    $[11] = isPreviousRowSelected;
    $[12] = isSelected;
    $[13] = onCheckboxClick;
    $[14] = onRowClick;
    $[15] = renderMenuEntries;
    $[16] = row;
    $[17] = rowIndex;
    $[18] = rowStyle;
    $[19] = showSelectionBoundary;
    $[20] = stickyColumnLayouts;
    $[21] = target;
    $[22] = variant;
    $[23] = T0;
    $[24] = T1;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
  } else {
    T0 = $[23];
    T1 = $[24];
    t3 = $[25];
    t4 = $[26];
    t5 = $[27];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[103] !== T0 || $[104] !== t3) {
    t6 = <T0>{t3}</T0>;
    $[103] = T0;
    $[104] = t3;
    $[105] = t6;
  } else {
    t6 = $[105];
  }
  let t7;
  if ($[106] !== T1 || $[107] !== t4 || $[108] !== t6) {
    t7 = <T1>{t4}{t6}</T1>;
    $[106] = T1;
    $[107] = t4;
    $[108] = t6;
    $[109] = t7;
  } else {
    t7 = $[109];
  }
  return t7;
};
export const FilterListRow = React.memo(FilterListRowInner, (prev, next) => prev.row.id === next.row.id && prev.row.original === next.row.original && prev.rowIndex === next.rowIndex && prev.isSelected === next.isSelected && prev.isPreviousRowSelected === next.isPreviousRowSelected && prev.isNextRowSelected === next.isNextRowSelected && prev.enableRowSelection === next.enableRowSelection && prev.href === next.href && prev.target === next.target && prev.onRowClick === next.onRowClick && prev.onCheckboxClick === next.onCheckboxClick && prev.hasClickHandler === next.hasClickHandler && prev.actionButtons === next.actionButtons && prev.actionButtonsDisplay === next.actionButtonsDisplay && prev.actionMenuTriggerVariant === next.actionMenuTriggerVariant && prev.getVisibleMenuItems === next.getVisibleMenuItems && prev.renderMenuEntries === next.renderMenuEntries && prev.horizontalOverflowState === next.horizontalOverflowState && prev.showSelectionBoundary === next.showSelectionBoundary && prev.stickyColumnLayouts === next.stickyColumnLayouts && prev.rowStyle === next.rowStyle && prev.variant === next.variant && prev.hideDivider === next.hideDivider) as typeof FilterListRowInner;
function _temp2(event) {
  event.stopPropagation();
  event.preventDefault();
}
function _temp3(event_0) {
  event_0.stopPropagation();
  event_0.preventDefault();
}
function _temp4(e) {
  e.stopPropagation();
  e.preventDefault();
}